import { useCallback } from 'react';
import { Button, InputAdornment, Stack, Typography, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate } from 'react-router';
import { LoadingButton } from '@mui/lab';

import { FormProvider, Iconify, RHFTextField } from '@app/components';
import { Strings } from '@app/constants';
import { PATH_AUTH } from '@app/routes/paths';
import { useRequestState } from '@app/hooks';
import type { IForgotPasswordSchema } from '../types';
import { forgotPasswordSchema } from '../validators';
import { forgotPassword } from '../slice';
import { selectForgotPasswordState } from '../selectors';
import { typography } from '@app/theme/typography';
import { SvgColor } from '@app/components/svg-color';

// --------------------------------------------

const ForgotPasswordForm = () => {
  const theme = useTheme();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const email = location.state.email || '';

  const methods = useForm<IForgotPasswordSchema>({
    resolver: yupResolver(forgotPasswordSchema),
    defaultValues: {
      email,
    },
  });

  const onSubmit = (form: IForgotPasswordSchema) => {
    dispatch(forgotPassword(form));
  };

  const { loading } = useRequestState({
    stateSelector: selectForgotPasswordState,
    errorShown: true,
    successMessageShown: true,
  });

  return (
    <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
      <Stack>
        <Typography
          fontFamily={typography.fontFamily}
          fontSize={{ xs: '12px', md: '15px' }}
          fontWeight={typography.fontWeightRegular}
        >
          {Strings.field.emailAddress}
        </Typography>
        <RHFTextField
          data-testId="login.email"
          name="email"
          type="email"
          fullWidth
          size="small"
          sx={{ marginBottom: 2, backgroundColor: theme.palette.grey[50], borderRadius: '10px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgColor fontSize={'10px'} src={`/assets/icons/ic_email.svg`} />
              </InputAdornment>
            ),
          }}
        />
        <LoadingButton
          size="large"
          variant="contained"
          fullWidth
          type="submit"
          loading={loading}
          sx={{
            marginTop: 5,
            borderRadius: '50px'
          }}
        >
          {Strings.button.continue}
        </LoadingButton>
        <Button
          href={PATH_AUTH.login}
          size="medium"
          fullWidth
          type="submit"
          sx={{
            textTransform: 'none',
            marginTop: 2,
            marginBottom: 10,
            color: theme.palette.common.black,
          }}
        >
          <Iconify icon="ic:twotone-arrow-back-ios" marginRight={1} width={19} />
          {Strings.button.backLogin}
        </Button>
      </Stack>
    </FormProvider>
  );
};

export { ForgotPasswordForm };
